// Campaign feature types — real backend (Faz 2). Article deseninin content-type
// klonu; author/tags/featured/readingTime YOK, kampanya alanları (startDate/
// endDate/ctaLink/publishDate) VAR. BaseStatus enum Article/Page/Sss ile aynı.

import { useTranslation } from 'react-i18next';
import type { ContentStatus } from '@shared/constants/statuses';

export type CampaignStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'ARCHIVED'
  | 'REJECTED'
  | 'DELETED';

export const CAMPAIGN_FILTER_STATUSES: CampaignStatus[] = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
  'REJECTED',
];

export function useCampaignStatusLabel(): (status: CampaignStatus) => string {
  const { t } = useTranslation(['campaigns', 'common']);
  const td = t as unknown as (key: string) => string;
  return (status) => td(`status.${status}`);
}

const STATUS_TO_NUM: Record<CampaignStatus, number> = {
  DELETED: 2,
  ARCHIVED: 3,
  DRAFT: 4,
  IN_REVIEW: 5,
  APPROVED: 6,
  PUBLISHED: 7,
  REJECTED: 8,
};

const NUM_TO_STATUS: Record<number, CampaignStatus> = {
  2: 'DELETED',
  3: 'ARCHIVED',
  4: 'DRAFT',
  5: 'IN_REVIEW',
  6: 'APPROVED',
  7: 'PUBLISHED',
  8: 'REJECTED',
};

export function campaignStatusFromNumber(n: number): CampaignStatus {
  return NUM_TO_STATUS[n] ?? 'DRAFT';
}

export function campaignStatusToNumber(s: CampaignStatus): number {
  return STATUS_TO_NUM[s];
}

/** Collapse onto the 4-state ContentStatus used by the shared StatusBadge. */
export function toContentStatus(s: CampaignStatus): ContentStatus {
  switch (s) {
    case 'DRAFT':
    case 'REJECTED':
      return 'DRAFT';
    case 'IN_REVIEW':
    case 'APPROVED':
      return 'REVIEW';
    case 'PUBLISHED':
      return 'PUBLISHED';
    case 'ARCHIVED':
    case 'DELETED':
      return 'ARCHIVED';
  }
}

export interface Campaign {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  categoryId: string | null;
  coverImageUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  ctaLink: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  /** MOD-08 — comma-separated SEO keywords (additive; null on legacy rows). */
  keywords: string | null;
  allowIndexing: boolean;
  status: CampaignStatus;
  publishedAt: string | null;
  /** Zamanlanmış yayın: Approved + due olduğunda job otomatik yayınlar. */
  publishDate: string | null;
  createdAt: string;
  updatedAt: string;
  languageId: string;
  groupId: string;
  variantLanguageCodes: string[];
}

export interface CampaignListItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  categoryId: string | null;
  coverImageUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  status: CampaignStatus;
  publishedAt: string | null;
  updatedAt: string;
  languageId: string;
  groupId: string;
  variantLanguageCodes: string[];
}

export interface CampaignVersion {
  id: string;
  campaignId: string;
  versionNumber: number;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  categoryId: string | null;
  coverImageUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  ctaLink: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  allowIndexing: boolean;
  authorUserId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

export interface CampaignListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: CampaignStatus;
  categoryId?: string;
  publishedOnly?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  descending?: boolean;
  languageId?: string;
}

/** Body for POST /campaigns and PUT /campaigns/{id}. */
export interface CampaignWriteInput {
  title: string;
  body: string;
  categoryId?: string | null;
  slug?: string | null;
  excerpt?: string | null;
  coverImageUrl?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  ctaLink?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  canonicalTag?: string | null;
  customCode?: string | null;
  /** MOD-08 — comma-separated SEO keywords (max 500 chars). */
  keywords?: string | null;
  allowIndexing?: boolean;
  changeNote?: string | null;
  // create-only i18n
  languageId?: string | null;
  groupId?: string | null;
  // update-only scheduled publish
  publishDate?: string | null;
}
